"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { Button } from "@heroui/button";
import { cn } from "@heroui/react";

export default function Error({
  error,
  reset,
}: {
  error: Error;
  reset: () => void;
}) {
  const [retryCount, setRetryCount] = useState(0);
  const [isShaking, setIsShaking] = useState(false);

  useEffect(() => {
    // Log the error to an error reporting service
    /* eslint-disable no-console */
    console.error(error);
  }, [error]);

  const handleReset = () => {
    setRetryCount((prev) => prev + 1);
    setIsShaking(true);
    setTimeout(() => setIsShaking(false), 500);
    reset();
  };

  const getRandomMessage = () => {
    const messages = [
      "Oops! Our security alpacas had a bit of a stumble! 🦙",
      "Houston, we have a problem... but our alpacas are on it! 🚀",
      "Error 404: Alpaca not found (but we found the bug!) 🔍",
      "The cyber-defense llamas need a coffee break ☕",
      "Something went sideways in the alpaca matrix! 🌐",
      "Our fuzzy security team is debugging this right now! 🐛",
      "Plot twist: Even alpacas make mistakes sometimes! 📚",
      "Critical error: Insufficient hay detected in server room 🌾",
    ];

    return messages[retryCount % messages.length];
  };

  const getRetryButtonText = () => {
    if (retryCount === 0) return "Wake up the alpacas! 🦙";
    if (retryCount === 1) return "Give them more coffee ☕";
    if (retryCount === 2) return "Try the magic restart button ✨";
    if (retryCount === 3) return "Shake the server gently 🫨";

    return "One more time, with feeling! 💪";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-800 flex items-center justify-center p-8">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(29,78,216,0.15),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_60%,rgba(147,51,234,0.1),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_20%,rgba(59,130,246,0.1),transparent_50%)]" />
      </div>

      <div className="relative max-w-2xl mx-auto text-center">
        {/* Animated Alpaca */}
        <div
          className={cn(
            "mb-8 transition-transform duration-500",
            isShaking && "animate-bounce",
          )}
        >
          <div className="relative w-32 h-32 mx-auto mb-6">
            <div className="absolute inset-0 bg-gradient-to-br from-red-400 via-orange-500 to-yellow-500 rounded-full opacity-80 animate-pulse" />
            <div className="absolute inset-2 bg-slate-900 rounded-full flex items-center justify-center">
              <Image
                alt="Confused alpaca"
                className="w-20 h-16 opacity-90"
                height={64}
                src="/illustrations/AlpacasFichier 39.png"
                width={80}
              />
            </div>
            {/* Error indicator */}
            <div className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center animate-pulse">
              <span className="text-white text-sm font-bold">!</span>
            </div>
          </div>
        </div>

        {/* Dynamic Error Message */}
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 animate-fadeIn">
          {getRandomMessage()}
        </h1>

        {/* Humorous Description */}
        <p className="text-xl text-gray-300 mb-8 leading-relaxed animate-fadeIn animation-delay-200">
          Don&apos;t worry, this happens to the best of us! Our cyber-security
          team of highly trained alpacas is already investigating the situation
          with their tiny detective hats on. 🕵️‍♂️
        </p>

        {/* Error Details (if in development) */}
        {process.env.NODE_ENV === "development" && (
          <div className="mb-8 p-4 bg-red-900/20 border border-red-500/30 rounded-lg text-left animate-fadeIn animation-delay-400">
            <h3 className="text-red-400 font-semibold mb-2">
              Developer Debug Info:
            </h3>
            <p className="text-red-300 text-sm font-mono break-all">
              {error.message}
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-4 animate-fadeIn animation-delay-600">
          <Button
            className={cn(
              "bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-bold py-4 px-8 text-lg rounded-xl transition-all duration-300 shadow-2xl hover:shadow-blue-500/25 border-0",
              isShaking && "animate-pulse",
            )}
            size="lg"
            variant="solid"
            onPress={handleReset}
          >
            {getRetryButtonText()}
          </Button>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              className="bg-slate-700 hover:bg-slate-600 text-white font-medium py-3 px-6 rounded-lg transition-all duration-300"
              size="md"
              variant="solid"
              onPress={() => (window.location.href = "/")}
            >
              🏠 Return to safety
            </Button>

            <Button
              className="bg-slate-700 hover:bg-slate-600 text-white font-medium py-3 px-6 rounded-lg transition-all duration-300"
              size="md"
              variant="solid"
              onPress={() => window.location.reload()}
            >
              🔄 Refresh the magic
            </Button>
          </div>
        </div>

        {/* Fun Facts */}
        <div className="mt-12 p-6 bg-blue-900/20 border border-blue-500/30 rounded-lg animate-fadeIn animation-delay-800">
          <h3 className="text-blue-400 font-semibold mb-3">
            🎉 Fun Alpaca Fact:
          </h3>
          <p className="text-blue-300 text-sm leading-relaxed">
            Did you know? Alpacas have three stomachs and can spit up to 10 feet
            when annoyed. Fortunately, our digital alpacas only spit out clean,
            secure code! 🦙✨
          </p>
        </div>

        {/* Retry Counter Easter Egg */}
        {retryCount > 3 && (
          <div className="mt-6 p-4 bg-yellow-900/20 border border-yellow-500/30 rounded-lg animate-bounce">
            <p className="text-yellow-300 text-sm">
              🏆 Persistence Level: {retryCount} - You&apos;re really committed
              to this! The alpacas are impressed! 🎯
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
